import { useState } from "react";
import { useAuth } from "@/components/auth/AuthProvider";
import { useDocuments } from "@/hooks/useDocuments";
import { SubscriptionCheck } from "@/components/documents/subscription/SubscriptionCheck";
import { DocumentList } from "./DocumentList";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Plus, FileText } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { DocumentGeneratorContent } from "./DocumentGeneratorContent";
import { motion, AnimatePresence } from "framer-motion";
import { DocumentGeneratorProvider } from "./DocumentGeneratorProvider";

export function DocumentsContent() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showNewDocumentDialog, setShowNewDocumentDialog] = useState(false);
  
  if (!user?.id || typeof user.id !== 'string' || user.id.trim() === '') {
    console.log("DocumentsContent: No valid user ID");
    return null;
  }

  const {
    documents,
    isLoading: isLoadingDocuments,
    error,
    deleteDocument,
  } = useDocuments(user.id);

  const handleDocumentGenerated = async () => {
    console.log("Document generated, refreshing list...");
    try {
      await queryClient.invalidateQueries({ queryKey: ['documents', user.id] });
      setShowNewDocumentDialog(false);
      toast({
        title: "Dokument opprettet",
        description: "Dokumentet er nå tilgjengelig i dokumentoversikten",
      });
    } catch (error) {
      console.error("Error refreshing documents:", error);
      toast({
        variant: "destructive",
        title: "Feil ved oppdatering",
        description: "Kunne ikke oppdatere dokumentoversikten. Prøv å laste siden på nytt.",
      });
    }
  };

  const handleDocumentDelete = async (documentId: string) => {
    try {
      await deleteDocument(documentId);
      await queryClient.invalidateQueries({ queryKey: ['documents', user.id] });
      toast({
        title: "Dokument slettet",
        description: "Dokumentet er nå fjernet fra oversikten",
      });
    } catch (error) {
      console.error("Failed to delete document:", error);
      toast({
        variant: "destructive",
        title: "Feil ved sletting",
        description: "Kunne ikke slette dokumentet. Vennligst prøv igjen.",
      });
    }
  };

  return (
    <SubscriptionCheck>
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="min-h-screen bg-gradient-to-b from-amber-50/50 to-white backdrop-blur-sm w-full relative pb-24 sm:pb-8"
      >
        <div className="container mx-auto px-3 py-4 sm:px-4 sm:py-6 space-y-4 sm:space-y-6 max-w-7xl">
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b pb-4 mb-6"
          >
            <motion.div 
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="flex flex-col gap-4"
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white/80 p-4 sm:p-6 rounded-lg border shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-full">
                    <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-amber-600" />
                  </div>
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-amber-600 to-amber-400 bg-clip-text text-transparent">
                      DokGen
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-md">
                      Opprett, administrer og organiser juridiske dokumenter.
                      Bruk våre maler eller lag egendefinerte dokumenter.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => setShowNewDocumentDialog(true)}
                  className="bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-700 hover:to-amber-600 text-white shadow-lg hover:shadow-xl transition-all duration-300 w-full sm:w-auto group"
                  size="lg"
                >
                  <Plus className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform" />
                  Opprett dokument
                </Button>
              </div>
            </motion.div>
          </motion.div>

          <Dialog 
            open={showNewDocumentDialog} 
            onOpenChange={setShowNewDocumentDialog}
          >
            <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-hidden flex flex-col p-0">
              <DocumentGeneratorProvider>
                <DocumentGeneratorContent
                  onClose={() => setShowNewDocumentDialog(false)}
                  onSuccess={handleDocumentGenerated}
                />
              </DocumentGeneratorProvider>
            </DialogContent>
          </Dialog>

          <AnimatePresence mode="wait">
            <motion.div 
              key="document-list"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ delay: 0.2 }}
              className="w-full"
            >
              <DocumentList 
                documents={documents || []}
                onDelete={handleDocumentDelete}
              />
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </SubscriptionCheck>
  );
}